import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, CalendarDays, Clock, Users, MessageCircle, Sparkles } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { format, parseISO } from "date-fns";
import { sv } from "date-fns/locale";
import affarsnatverketLogo from "@/assets/affarsnatverket-logo.png";
import ZoomJoinCard from "@/components/ZoomJoinCard";
import { Button } from "@/components/ui/button";
import {
  ParticipantAvatar,
  type PublicParticipant,
} from "@/components/calendar/ParticipantList";
import { TooltipProvider } from "@/components/ui/tooltip";
import { isEventPast } from "@/lib/eventTime";

const NEXT_MEETING_FALLBACK = {
  date: "",
  time: "",
  title: "Digital nätverksträff",
};

const points = [
  "Företagare, säljare och rådgivare",
  "Samtal i mindre grupper",
  "150 kr per träff",
  "Inget medlemskap",
  "Ingen bindningstid",
  "Relationer som håller över tid",
];


interface NextMeeting {
  id: string;
  slug: string;
  title: string;
  dateLabel: string | null;
  time: string | null;
}

const Hero = () => {
  const [meeting, setMeeting] = useState<NextMeeting | null>(null);
  const [participants, setParticipants] = useState<PublicParticipant[]>([]);
  const [loading, setLoading] = useState(true);
  const [upcoming, setUpcoming] = useState<
    { id: string; slug: string; title: string; date: Date; time: string | null }[]
  >([]);

  useEffect(() => {
    (async () => {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const { data } = await supabase
        .from("calendar_events_public")
        .select("id, slug, title, event_date, event_time, end_time, event_type, status, display_order")
        .eq("status", "published")
        .eq("event_type", "foretagsnatverk")
        .gte("event_date", format(today, "yyyy-MM-dd"))
        .order("event_date", { ascending: true })
        .order("display_order", { ascending: true })
        .limit(8);

      type Ev = {
        id: string;
        slug: string;
        title: string;
        event_date: string;
        event_time: string | null;
        end_time: string | null;
      };
      const list = ((data || []) as Ev[]).filter(
        (e) => !isEventPast(e.event_date, e.event_time, e.end_time),
      );
      setUpcoming(
        list.slice(1, 5).map((e) => ({
          id: e.id,
          slug: e.slug,
          title: e.title,
          date: parseISO(e.event_date),
          time: e.event_time ? e.event_time.slice(0, 5) : null,
        })),
      );

      if (list.length > 0) {
        const ev = list[0];
        setMeeting({
          id: ev.id,
          slug: ev.slug,
          title: ev.title,
          dateLabel: format(parseISO(ev.event_date), "EEEE d MMMM", { locale: sv }),
          time: ev.event_time ? ev.event_time.slice(0, 5) : null,
        });
        const { data: participantData } = await supabase
          .from("event_participants_public")
          .select("id, event_id, first_name, last_name, company, photo_url, linkedin_url")
          .eq("event_id", ev.id)
          .limit(8);
        setParticipants((participantData || []) as PublicParticipant[]);
      } else if (NEXT_MEETING_FALLBACK.date) {
        setMeeting({
          id: "",
          slug: "",
          title: NEXT_MEETING_FALLBACK.title,
          dateLabel: format(parseISO(NEXT_MEETING_FALLBACK.date), "EEEE d MMMM", { locale: sv }),
          time: NEXT_MEETING_FALLBACK.time || null,
        });
      } else {
        setMeeting(null);
      }
      setLoading(false);
    })();
  }, []);

  return (
    <section className="relative overflow-hidden bg-primary pt-24 md:pt-32 pb-16 md:pb-20 px-5 md:px-4">
      <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmZmZmYiIGZpbGwtb3BhY2l0eT0iMC4wMyI+PGNpcmNsZSBjeD0iMzAiIGN5PSIzMCIgcj0iMiIvPjwvZz48L2c+PC9zdmc+')] opacity-50" />

      <div className="container mx-auto relative z-10 px-0 md:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-14 items-center">
          {/* Left column – text */}
          <div className="animate-fade-in max-w-xl min-w-0 w-full">
            <img
              src={affarsnatverketLogo}
              alt="Affärsnätverket logotyp"
              className="h-16 md:h-20 w-auto mb-6"
            />

            <div className="inline-flex items-center gap-2 bg-accent/20 text-accent px-4 py-2 rounded-full mb-5 text-sm font-medium">
              <Sparkles className="h-4 w-4" />
              Affärsnätverket
            </div>

            <h1 className="text-[clamp(1.75rem,8vw,2rem)] sm:text-5xl md:text-6xl font-bold text-white mb-5 leading-[1.1] sm:leading-[1.05] break-words">
              Mycket trevligt{" "}
              <span className="text-accent">företagsnätverk med stor personlighet</span>
              {" "}och välkomnande känsla
            </h1>

            <p className="text-base md:text-lg text-white/80 leading-relaxed mb-6">
              Affärsnätverket driver digitala nätverksträffar för företagare, säljare
              och rådgivare som vill bygga relationer, hitta nya kontakter och
              skapa affärsmöjligheter utan kalla pitchar.
            </p>

            <ul className="space-y-2.5 mb-7">
              {points.map((point) => (
                <li key={point} className="flex items-center gap-3 text-white/90">
                  <span className="flex-shrink-0 w-5 h-5 rounded-full bg-accent/25 flex items-center justify-center">
                    <span className="w-1.5 h-1.5 rounded-full bg-accent" />
                  </span>
                  <span className="text-base">{point}</span>
                </li>
              ))}
            </ul>

            <Link
              to="/kalender"
              className="group inline-flex w-fit items-center gap-2 text-base font-medium text-white hover:text-accent transition-colors"
            >
              Se alla träffar i kalendern
              <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
              <span className="sr-only">Gå till kalendern</span>
            </Link>
          </div>

          {/* Right column – kort för nästa träff */}
          <div className="animate-fade-in min-w-0" style={{ animationDelay: "120ms" }}>
            <div className="relative rounded-2xl bg-card shadow-xl p-7 md:p-8">
              <div className="flex items-start gap-4 mb-5">
                <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-accent flex items-center justify-center">
                  <CalendarDays className="w-6 h-6 text-white" />
                </div>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wider text-accent">
                    Nästa digitala nätverksträff
                  </p>
                  <p className="text-sm text-muted-foreground mt-1">
                    Digitala träffar sedan 2019
                  </p>
                </div>
              </div>

              {loading ? (
                <div className="h-7 w-40 bg-muted/60 rounded animate-pulse mb-3" />
              ) : meeting ? (
                <div className="mb-4">
                  {meeting.slug ? (
                    <Link
                      to={`/kalender/${meeting.slug}`}
                      className="group inline-flex items-center gap-2 text-2xl md:text-3xl font-bold text-primary capitalize leading-tight hover:text-accent"
                    >
                      {meeting.dateLabel}
                      <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
                    </Link>
                  ) : (
                    <p className="text-2xl md:text-3xl font-bold text-primary capitalize leading-tight">
                      {meeting.dateLabel}
                    </p>
                  )}
                  <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-muted-foreground">
                    {meeting.time && (
                      <span className="inline-flex items-center gap-1.5">
                        <Clock className="w-4 h-4 text-accent" />
                        kl {meeting.time}
                      </span>
                    )}
                    <span className="inline-flex items-center gap-1.5">
                      <MessageCircle className="w-4 h-4 text-accent" />
                      {meeting.title}
                    </span>
                  </div>
                </div>
              ) : (
                <p className="text-2xl md:text-3xl font-bold text-primary leading-tight mb-4">
                  Nästa datum annonseras snart
                </p>
              )}

              <p className="text-base text-muted-foreground leading-relaxed mb-6">
                Presentera dig, träffa andra företagare och hitta nya samtal i
                mindre grupper.
              </p>

              {meeting?.slug && participants.length > 0 && (
                <TooltipProvider delayDuration={150}>
                  <div className="mb-5" aria-label="Några som planerar att vara med">
                    <p className="mb-2 text-sm font-medium text-foreground">
                      Några som planerar att vara med
                    </p>
                    <div className="flex flex-wrap gap-2" role="list">
                      {participants.map((participant) => (
                        <div key={participant.id} role="listitem">
                          <ParticipantAvatar participant={participant} />
                        </div>
                      ))}
                    </div>
                  </div>
                </TooltipProvider>
              )}

              {meeting?.slug && (
                <Button asChild className="mb-5 w-full sm:w-auto">
                  <Link to={`/kalender/${meeting.slug}#visa-deltagande`}>
                    <Users className="mr-2 h-4 w-4" />
                    Visa att du planerar att delta
                  </Link>
                </Button>
              )}

              {upcoming.length > 0 && (
                <div className="mb-5 border-t border-border/50 pt-4">
                  <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-accent">
                    Kommande träffar
                  </p>
                  <ul className="space-y-2">
                    {upcoming.map((ev) => (
                      <li key={ev.id}>
                        <Link
                          to={`/kalender/${ev.slug}`}
                          className="group flex items-center gap-3 rounded-lg p-1.5 -mx-1.5 hover:bg-muted/60 transition-colors"
                        >
                          <span className="flex w-11 flex-shrink-0 flex-col items-center rounded-md bg-primary py-1 leading-none text-primary-foreground">
                            <span className="text-base font-bold">{format(ev.date, "d")}</span>
                            <span className="text-[10px] uppercase">
                              {format(ev.date, "MMM", { locale: sv }).replace(".", "")}
                            </span>
                          </span>
                          <span className="min-w-0 flex-1">
                            <span className="block text-sm font-semibold text-foreground">
                              <span className="capitalize">{format(ev.date, "EEEE d MMMM", { locale: sv })}</span>
                              {ev.time && <span className="font-normal text-muted-foreground"> · kl {ev.time}</span>}
                            </span>
                            <span className="block truncate text-xs text-muted-foreground">{ev.title}</span>
                          </span>
                          <ArrowRight className="h-4 w-4 flex-shrink-0 text-muted-foreground transition-transform group-hover:translate-x-1 group-hover:text-accent" />
                        </Link>
                      </li>
                    ))}
                  </ul>
                  <Link
                    to="/kalender"
                    className="mt-3 inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:text-accent"
                  >
                    Se alla träffar i kalendern
                    <ArrowRight className="h-4 w-4" />
                  </Link>
                </div>
              )}

              <div className="flex items-center justify-between border-t border-border/50 pt-4">
                <span className="inline-flex items-center gap-2 text-sm text-muted-foreground">
                  <Users className="w-4 h-4 text-accent" />
                  Öppet för alla företagare
                </span>
                {meeting?.slug && (
                  <Link
                    to={`/kalender/${meeting.slug}`}
                    className="group inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:text-accent"
                  >
                    Visa träffen
                    <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                  </Link>
                )}
              </div>
            </div>

            <div className="mt-5">
              <ZoomJoinCard />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
